'use client';

import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import NumberFlow from '@number-flow/react';
import { motion, useInView } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';

import Testimonials from '@/components/sections/testimonials';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { SubtleEntrance } from '@/components/ui/subtle-entrance';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------------- */
/*  Hero – sand-toned subpage hero                                            */
/* -------------------------------------------------------------------------- */

// Real, relatable photos — people, not abstract renders or stray stock shots.
const TEAM_PHOTOS = [
  '/images/about/4.webp', // a team sharing a laugh
  '/images/about/3.webp', // people collaborating
];

const Hero = () => {
  const { t, locale } = useTranslation();

  return (
    <section className="bg-[#fafaf9] pt-32 pb-16 md:pt-40 md:pb-24">
      <div className="container">
        <SubtleEntrance>
          <div className="mb-12 max-w-3xl md:mb-16">
            <p className="mb-4 text-xs font-medium tracking-widest text-neutral-500 uppercase">
              {locale === 'ar' ? 'عن Open' : 'About Open'}
            </p>
            <h1 className="font-serif text-balance text-neutral-900 text-[2rem] md:text-4xl xl:text-5xl">
              {t.about["Building AI that doesn't disappoint"]}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {
                t.about[
                  '77% automation. 95%+ accuracy. Actually solves problems.'
                ]
              }
            </p>
            <Link
              href="#careers"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800"
            >
              {t.about['Join the team']}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </SubtleEntrance>

        {/* Photo grid */}
        <SubtleEntrance delay={0.15}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            {TEAM_PHOTOS.map((src) => (
              <div
                key={src}
                className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-neutral-200 ring-1 ring-neutral-200/60"
              >
                <Image
                  src={src}
                  alt=""
                  fill
                  sizes="(max-width: 640px) 100vw, 50vw"
                  className="object-cover transition-transform duration-700 hover:scale-[1.03]"
                />
              </div>
            ))}
          </div>
        </SubtleEntrance>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*  Founder's message                                                         */
/* -------------------------------------------------------------------------- */

const FoundersMessage = () => {
  const { t, locale } = useTranslation();

  return (
    <section className="bg-white py-24 md:py-32">
      <div className="container">
        <SubtleEntrance>
          <div className="mx-auto max-w-3xl">
            <p className="mb-6 text-xs font-medium tracking-widest text-neutral-500 uppercase">
              {locale === 'ar' ? 'كلمة من المؤسس' : 'A note from the founder'}
            </p>

            <div className="flex items-center gap-4">
              <div className="relative h-14 w-14 overflow-hidden rounded-full ring-1 ring-neutral-200">
                <Image
                  src="/about/image.png"
                  alt={t.about['Mo Gharbat']}
                  fill
                  className="object-cover"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-900">
                  {t.about['Mo Gharbat']}
                </p>
                <p className="text-xs text-neutral-500">{t.about.CEO}</p>
              </div>
            </div>

            <div className="mt-10 space-y-7 text-lg leading-relaxed text-neutral-700 md:text-xl md:leading-[1.7]">
              <p>
                {
                  t.about[
                    'Companies handle customer communication with specialized teams. Support agents answer tickets. QA teams check quality. Analysts extract insights. Specialists handle proactive outreach.'
                  ]
                }{' '}
                {
                  t.about[
                    "It's really just a necessity. Each role exists because the work was too hard for one person to do well. Everyone is doing the same thing—talking to customers—but the technology forced them apart."
                  ]
                }
              </p>
              <p>
                {
                  t.about[
                    'That constraint is disappearing. AI can now do what used to require different types of human expertise: understand context, maintain quality, extract insights, reach out proactively.'
                  ]
                }{' '}
                {
                  t.about[
                    'Not in theory—in practice, today. Open is what happens when you stop fragmenting customer communication and let it be what it should have been all along: one coherent system that learns, responds, and improves itself.'
                  ]
                }{' '}
                {
                  t.about[
                    "The surprising part isn't that AI can do this. It's that it does it with the empathy and nuance people thought only humans could provide."
                  ]
                }
              </p>
            </div>

            <div className="mt-10 border-t border-neutral-200 pt-8">
              <p
                className="text-3xl text-neutral-900 md:text-4xl"
                style={{ fontFamily: 'var(--font-league-script)' }}
              >
                {t.about['Mo Gharbat']}
              </p>
            </div>
          </div>
        </SubtleEntrance>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*  Stats / Value section                                                     */
/* -------------------------------------------------------------------------- */

const StatsSection = () => {
  const { t, locale } = useTranslation();
  const [automationRate, setAutomationRate] = useState(0);
  const [csatScore, setCsatScore] = useState(0);
  const [softwaresReplaced, setSoftwaresReplaced] = useState(0);
  const [responseTime, setResponseTime] = useState(0);

  const ref = useRef(null);
  const isInView = useInView(ref);

  useEffect(() => {
    if (isInView) {
      setAutomationRate(77);
      // 90, not the 68 this counted to until 2026-09-20. Every other surface
      // on the site — navbar, testimonials, the OTO story, the metadata
      // descriptions — says 90%+ CSAT, so this page was the one place quoting
      // a different score for the same metric, and quoting the lower one.
      setCsatScore(90);
      setSoftwaresReplaced(4);
      setResponseTime(30);
    }
  }, [isInView]);

  // Unit glyph only — every stat value below is unchanged. `%` is script
  // neutral and stays; `s` is an English abbreviation and does not.
  const seconds = locale === 'ar' ? 'ث' : 's';

  const stats = [
    {
      value: automationRate,
      suffix: '%',
      label: t.about['Avg automation rate'],
    },
    {
      value: csatScore,
      suffix: '%',
      label: t.about['All-time high CSAT'],
    },
    {
      value: softwaresReplaced,
      suffix: '',
      label: t.about['Softwares replaced'],
    },
    {
      value: responseTime,
      suffix: seconds,
      label: t.about['Avg response time'],
    },
  ];

  return (
    <section ref={ref} className="bg-[#fafaf9] py-24 md:py-32">
      <div className="container">
        <SubtleEntrance>
          <div className="mb-14 max-w-2xl md:mb-20">
            <p className="mb-4 text-xs font-medium tracking-widest text-neutral-500 uppercase">
              {locale === 'ar' ? 'بالأرقام' : 'By the numbers'}
            </p>
            <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
              {t.about["We're in the business of value delivery"]}
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {
                t.about[
                  "Agent 5 doesn't just automate tickets. It handles the work that actually matters—resolving customer issues, maintaining quality, and delivering results that used to require entire teams."
                ]
              }
            </p>
            <Link
              href="/agent5"
              className="group mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-neutral-900 underline decoration-neutral-300 underline-offset-4 transition-colors hover:decoration-neutral-900"
            >
              {t.about['See Agent 5 in Action']}
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </SubtleEntrance>

        <SubtleEntrance delay={0.1}>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-neutral-200 bg-neutral-200 md:grid-cols-4">
            {stats.map((stat, i) => (
              <div key={i} className="flex flex-col gap-2 bg-white p-8 md:p-10">
                <div className="font-sans text-4xl font-medium text-neutral-900 md:text-5xl">
                  {/* NumberFlow renders `NaN` in its digit slot when handed an
                      Arabic-Indic numbering locale, so the counters keep Latin
                      digits. Only the unit suffix is localised. */}
                  <NumberFlow value={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-sm text-neutral-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </SubtleEntrance>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*  Careers                                                                   */
/* -------------------------------------------------------------------------- */

const CAREERS_URL = 'https://careers.open.cx';

const Careers = () => {
  const { t, isRTL, locale } = useTranslation();
  const isAr = locale === 'ar';

  return (
    <section id="careers" className="bg-white py-24 md:py-32">
      <div className="container">
        <SubtleEntrance>
          <div className="mb-4">
            <div className="max-w-2xl">
              <p className="mb-4 text-xs font-medium tracking-widest text-neutral-500 uppercase">
                {isAr ? 'الوظائف' : 'Careers'}
              </p>
              <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
                {t.about['Build the future of']}{' '}
                {t.about['customer communication']}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-neutral-500">
                {isAr
                  ? 'انتقلت جميع الوظائف المفتوحة إلى موقع التوظيف الجديد. استعرض الأدوار وقدّم من هناك.'
                  : 'All open roles now live on our careers site. Browse the openings and apply there.'}
              </p>
              <a
                href={CAREERS_URL}
                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800"
              >
                {isAr ? 'استعرض الوظائف المفتوحة' : 'View open roles'}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>

              <Dialog>
                <DialogTrigger asChild>
                  <button
                    className={cn(
                      'group mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-neutral-700 underline decoration-neutral-300 underline-offset-4 transition-colors hover:text-neutral-900 hover:decoration-neutral-900',
                      isRTL && 'flex-row-reverse',
                    )}
                  >
                    {t.about['Learn more about our hiring process']}
                    {isRTL ? (
                      <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
                    ) : (
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    )}
                  </button>
                </DialogTrigger>
                <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl">
                      {t.about['How we hire']}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                      {isAr
                        ? 'تعرّف على طريقة التوظيف في Open وعلى اللي نبحث عنه في المرشحين'
                        : "Learn about Open's hiring process and what we look for in candidates"}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 text-sm leading-relaxed">
                    <p>
                      {isAr
                        ? 'هناك شيء مختلف في الأشخاص اللي يريدون بناء أشياء لها قيمة. مو ممن يلاحقون المسميات الوظيفية أو يزيّنون سِيَرهم الذاتية، بل ممن يهتمون بالجودة لأنهم ما يعرفون طريقة أخرى للعمل، وممن يتحمسون لحل المشكلات الصعبة، لا لأن أحداً طلب منهم ذلك، بل لأن المشكلة نفسها مثيرة للاهتمام.'
                        : "There's something different about people who want to build things that matter. Not the ones chasing titles or optimizing for resume lines. The ones who care about quality because they can't help it. Who get excited about solving hard problems, not because someone told them to, but because the problem is interesting."}
                    </p>
                    <p>
                      {isAr
                        ? 'إن كنت تقرأ هذا، فالأرجح أنك واحد من هؤلاء. تريد تعمل على شيء حقيقي، شيء يساعد الناس فعلاً ما يكتفي بالادّعاء. رأيت من منتجات الذكاء الاصطناعي الجوفاء ما يكفي عشان تميّز بين التسويق والجوهر. وتريد تكون جزءاً من بناء الجوهر.'
                        : "If you're reading this, you're probably one of those people. You want to work on something real. Something that actually helps people instead of just claiming to. You've seen enough bullshit AI products that you can smell the difference between marketing and substance. And you want to be part of building the latter."}
                    </p>
                    <p>
                      {isAr
                        ? 'Open موجود لأن التواصل مع العملاء معطوب وما أحد يصلحه كما ينبغي. ما تعنينا التحسينات التدريجية ولا اللحاق بميزات المنافسين. نبني النظام اللي كان يفترض أن يوجد من البداية — ذكاء اصطناعي يشتغل فعلاً، بالجودة والموثوقية اللي تتوقعها من برنامج تأتمنه على عملائك.'
                        : "Open exists because customer communication is broken and nobody's fixing it right. We're not interested in incremental improvements or feature parity. We're building the system that should have existed from the start—AI that actually works, with the quality and reliability you'd expect from software you'd trust with your own customers."}
                    </p>
                    <p>
                      {isAr
                        ? 'وهذا مو سهل. يتطلب أشخاصاً يهتمون بالإتقان، ويدركون أن المنتجات العظيمة تأتي من فرق عظيمة، وأن الفرق العظيمة تنشأ بهدوء وتأنٍّ، بأشخاص يشاركوننا الهوس نفسه بالجودة.'
                        : "That's hard. It requires people who care about craft. Who understand that great products come from great teams, and great teams are built slowly, deliberately, with people who share the same obsession with quality."}
                    </p>
                    <div className="pt-4">
                      <h3 className="mb-3 text-lg">
                        {isAr ? 'الخطوات' : 'The Process'}
                      </h3>
                      <p className="mb-4">
                        {isAr
                          ? 'نُبقيها بسيطة لأننا نقدّر وقتك ووقتنا. وإليك كيف تسير العملية:'
                          : "We keep it simple because we value your time and ours. Here's how it works:"}
                      </p>
                      <ol className="list-inside list-decimal space-y-3">
                        <li>
                          <strong>
                            {isAr ? 'مكالمة أولية' : 'Screening call'}
                          </strong>{' '}
                          {isAr
                            ? '— تتحدث مع أحد المؤسسين. نريد نفهم وش يحرّكك، وما إذا كنا نحل مشكلات تهمّك.'
                            : "— You'll talk with one of the founders. We want to understand what drives you and whether we're solving problems you care about."}
                        </li>
                        <li>
                          <strong>{isAr ? 'مهمة' : 'Task'}</strong>{' '}
                          {isAr
                            ? '— مشكلة حقيقية نواجهها بالفعل. مو لغز على السبورة ولا تمارين leetcode، بل شيء يُظهر كيف تفكّر وكيف تعمل.'
                            : "— A real problem we're actually facing. Not a whiteboard puzzle or leetcode grind. Something that shows how you think and work."}
                        </li>
                        <li>
                          <strong>
                            {isAr ? 'المقابلة الأولى' : 'First interview'}
                          </strong>{' '}
                          {isAr
                            ? '— جلسة معمّقة مع المؤسسين. نتحدث عن عملك وعملنا، وعمّا إذا كان هذا التوافق مناسباً للطرفين.'
                            : "— Deep dive with the founders. We'll talk about your work, our work, and whether this is the right fit for both of us."}
                        </li>
                        <li>
                          <strong>
                            {isAr ? 'المقابلة الثانية' : 'Second interview'}
                          </strong>{' '}
                          {isAr
                            ? '— وقت أطول مع المؤسسين، يتركّز عادةً على القيم والثقافة وطريقة عملنا معاً.'
                            : '— More time with founders, usually focused on values, culture, and how we work together.'}
                        </li>
                        <li>
                          <strong>
                            {isAr ? 'تجربة مدفوعة' : 'Paid trial'}
                          </strong>{' '}
                          {isAr
                            ? '(لبعض الأدوار) — حسب المنصب والمستوى، قد نجري تجربة بدوام جزئي مدفوعة بالكامل. تعمل معنا فعلياً، وتتقاضى أجرك عليها، وتشوف إن كان التوافق قائماً.'
                            : '(for some roles) — Depending on the position and level, we might do a fully-paid part-time trial. Work with us for real, get paid for it, see if it clicks.'}
                        </li>
                        <li>
                          <strong>{isAr ? 'العرض' : 'Offer'}</strong>{' '}
                          {isAr
                            ? '— إذا كان الحماس متبادلاً، نجعلها رسمية.'
                            : "— If we're both excited, we make it official."}
                        </li>
                      </ol>
                    </div>
                    <p className="pt-6 text-center font-medium">
                      {isAr
                        ? 'إذا وجدت نفسك في هذا الكلام، قدّم. نبحث عن أشخاص يريدون بناء مستقبل التواصل مع العملاء، لا عن أشخاص يملأون مقعداً شاغراً.'
                        : "If this resonates, apply. We're looking for people who want to build the future of customer communication, not just fill a role."}
                    </p>
                    <div className="mt-8 rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-4">
                      <p className="text-center text-xs text-neutral-500">
                        {isAr
                          ? '🎁 مفاجأة: إن كنت تقرأ هذا، راسل '
                          : "🎁 Easter egg: If you're reading this, email "}
                        <span className="font-mono font-medium text-neutral-900">
                          mo+skip@open.cx
                        </span>{' '}
                        {isAr ? 'عشان تتخطى الطابور.' : 'to skip the queue!'}
                      </p>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </SubtleEntrance>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

export default function AboutOpenPage() {
  return (
    <>
      <Hero />
      <FoundersMessage />
      <StatsSection />
      <Careers />
      <Testimonials />
    </>
  );
}
